// Phase 10 — global socket-status hook + banner.
//
// Maintains a singleton socket connection that other hooks can observe
// without each one opening their own. Exposes:
//
//   - status: 'connected' | 'reconnecting' | 'disconnected' | 'failed'
//   - lastConnectedAt: Date | null
//   - reconnectAttempts: number
//
// Reconnect strategy: socket.io's default exponential backoff, capped
// at 30s. After 5 consecutive failures we flip to 'failed' so the UI
// can surface a "stale dashboard" warning even while the lib keeps
// retrying in the background.

import { useEffect, useState } from 'react';
import { io, Socket } from 'socket.io-client';

const API_URL = import.meta.env.VITE_API_URL || '';

let _singleton: Socket | null = null;
let _listeners: Array<(s: SocketState) => void> = [];
let _state: SocketState = {
  status: 'disconnected',
  lastConnectedAt: null,
  reconnectAttempts: 0,
};

export type SocketStatus = 'connected' | 'reconnecting' | 'disconnected' | 'failed';
export interface SocketState {
  status: SocketStatus;
  lastConnectedAt: Date | null;
  reconnectAttempts: number;
}

function _emit() {
  for (const l of _listeners) try { l(_state); } catch {}
}

export function getSocket(): Socket {
  if (_singleton) return _singleton;
  const token = localStorage.getItem('dk_token');
  _singleton = io(API_URL, {
    transports: ['websocket', 'polling'],
    auth: token ? { token } : undefined,
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 30000,
    timeout: 20000,
  });

  _singleton.on('connect', () => {
    _state = { status: 'connected', lastConnectedAt: new Date(), reconnectAttempts: 0 };
    _emit();
  });
  _singleton.on('disconnect', () => {
    _state = { ..._state, status: 'disconnected' };
    _emit();
  });
  _singleton.io.on('reconnect_attempt', (n: number) => {
    _state = { ..._state, status: n >= 5 ? 'failed' : 'reconnecting', reconnectAttempts: n };
    _emit();
  });
  _singleton.io.on('reconnect', () => {
    _state = { status: 'connected', lastConnectedAt: new Date(), reconnectAttempts: 0 };
    _emit();
  });
  _singleton.io.on('reconnect_failed', () => {
    _state = { ..._state, status: 'failed' };
    _emit();
  });

  return _singleton;
}

export function useSocketStatus(): SocketState {
  // Ensure the singleton is initialized — but only if the user is logged in.
  const [snapshot, setSnapshot] = useState<SocketState>(_state);
  useEffect(() => {
    const token = localStorage.getItem('dk_token');
    if (token) getSocket();           // ensure singleton exists
    const sub = (s: SocketState) => setSnapshot(s);
    _listeners.push(sub);
    return () => { _listeners = _listeners.filter(l => l !== sub); };
  }, []);
  return snapshot;
}

// Phase 10: after a refresh-token rotation, the api client gets a new
// access token. The socket should re-authenticate so the org-room
// subscription survives the rotation. Callers from the api client can
// invoke this after a successful /refresh.
export function reauthSocket() {
  if (!_singleton) return;
  const token = localStorage.getItem('dk_token');
  if (!token) return;
  // Update the auth payload and force a reconnect.
  (_singleton as any).auth = { token };
  try { _singleton.disconnect(); _singleton.connect(); } catch {}
}
